import { useMemo, useState, useCallback } from 'react';
import { ResumenService } from '../services/resumen.service';
import { useIngresos } from '../../modules/ingresos/hooks/useIngresos';
import { useEgresos } from '../../modules/egresos/hooks/useEgresos';
import { useInversiones } from '../../modules/inversiones/hooks/useInversiones';

export const useResumenes = () => {
  const [selectedMonth, setSelectedMonth] = useState<{ año: number; mes: number }>(() => {
    const now = new Date();
    return { año: now.getFullYear(), mes: now.getMonth() };
  });
  
  const { ingresos } = useIngresos();
  const { egresos } = useEgresos();
  const { inversiones } = useInversiones();
  
  const resumenService = useMemo(() => new ResumenService(), []);
  
  // Resumen del mes actual
  const resumenActual = useMemo(() => {
    return resumenService.getCurrentMonthResumen(ingresos, egresos, inversiones);
  }, [ingresos, egresos, inversiones, resumenService]);
  
  // Resumen del mes seleccionado
  const resumenSeleccionado = useMemo(() => {
    if (selectedMonth.año === new Date().getFullYear() && 
        selectedMonth.mes === new Date().getMonth()) {
      return resumenActual;
    }
    
    return resumenService.generateResumenMensual(
      selectedMonth.año,
      selectedMonth.mes,
      ingresos,
      egresos,
      inversiones
    );
  }, [selectedMonth, ingresos, egresos, inversiones, resumenService, resumenActual]);
  
  // Historial de resúmenes (últimos 12 meses)
  const historialResumenes = useMemo(() => {
    return resumenService.getResumenesHistory(ingresos, egresos, inversiones, 12);
  }, [ingresos, egresos, inversiones, resumenService]);
  
  // Datos para gráficos comparativos
  const datosComparativos = useMemo(() => {
    const meses = historialResumenes.map(resumen => ({
      mes: `${resumen.mes + 1}/${resumen.año}`,
      ingresos: resumen.totalIngresos,
      egresos: resumen.totalEgresos,
      balance: resumen.balance,
      patrimonio: resumen.patrimonioNeto,
      ahorro: resumen.porcentajeAhorro
    }));
    
    return meses;
  }, [historialResumenes]);
  
  // Tendencias y métricas agregadas
  const tendencias = useMemo(() => {
    if (historialResumenes.length < 2) {
      return {
        tendenciaIngresos: 'neutral',
        tendenciaEgresos: 'neutral',
        tendenciaBalance: 'neutral',
        promedioAhorro: 0,
        mejorMes: null,
        peorMes: null
      };
    }
    
    const ultimosTres = historialResumenes.slice(-3);
    const promedioIngresos = ultimosTres.reduce((sum, r) => sum + r.totalIngresos, 0) / ultimosTres.length;
    const promedioEgresos = ultimosTres.reduce((sum, r) => sum + r.totalEgresos, 0) / ultimosTres.length;
    const promedioBalance = ultimosTres.reduce((sum, r) => sum + r.balance, 0) / ultimosTres.length;
    const promedioAhorro = ultimosTres.reduce((sum, r) => sum + r.porcentajeAhorro, 0) / ultimosTres.length;
    
    const mejorMes = historialResumenes.reduce((mejor, actual) => 
      actual.balance > mejor.balance ? actual : mejor
    );
    
    const peorMes = historialResumenes.reduce((peor, actual) => 
      actual.balance < peor.balance ? actual : peor
    );
    
    // Calcular tendencias (comparar últimos 3 meses con 3 anteriores)
    const primerosTres = historialResumenes.slice(-6, -3);
    const promedioIngresosAnterior = primerosTres.length > 0 
      ? primerosTres.reduce((sum, r) => sum + r.totalIngresos, 0) / primerosTres.length 
      : promedioIngresos;
    const promedioEgresosAnterior = primerosTres.length > 0 
      ? primerosTres.reduce((sum, r) => sum + r.totalEgresos, 0) / primerosTres.length 
      : promedioEgresos;
    const promedioBalanceAnterior = primerosTres.length > 0 
      ? primerosTres.reduce((sum, r) => sum + r.balance, 0) / primerosTres.length 
      : promedioBalance;
    
    const getTendencia = (actual: number, anterior: number) => {
      const cambio = ((actual - anterior) / Math.abs(anterior)) * 100;
      if (cambio > 5) return 'positiva';
      if (cambio < -5) return 'negativa';
      return 'neutral';
    };
    
    return {
      tendenciaIngresos: getTendencia(promedioIngresos, promedioIngresosAnterior),
      tendenciaEgresos: getTendencia(promedioEgresos, promedioEgresosAnterior),
      tendenciaBalance: getTendencia(promedioBalance, promedioBalanceAnterior),
      promedioAhorro,
      mejorMes,
      peorMes
    };
  }, [historialResumenes]);
  
  // Insights consolidados
  const insightsConsolidados = useMemo(() => {
    const insights: string[] = [];
    
    // Insights de tendencias
    if (tendencias.tendenciaBalance === 'positiva') {
      insights.push('Tu situación financiera ha mejorado en los últimos meses');
    } else if (tendencias.tendenciaBalance === 'negativa') {
      insights.push('Tu balance ha empeorado en los últimos meses, considera revisar tus gastos');
    }
    
    // Insights de ahorro
    if (tendencias.promedioAhorro >= 20) {
      insights.push('Mantienes un excelente nivel de ahorro promedio');
    } else if (tendencias.promedioAhorro >= 10) {
      insights.push('Tu nivel de ahorro es bueno, pero puede mejorar');
    } else if (tendencias.promedioAhorro > 0) {
      insights.push('Tu capacidad de ahorro es limitada, considera optimizar gastos');
    } else {
      insights.push('Necesitas urgentemente revisar tus finanzas para generar ahorro');
    }
    
    // Insights comparativos
    if (tendencias.mejorMes && tendencias.peorMes) {
      const diferencia = tendencias.mejorMes.balance - tendencias.peorMes.balance;
      insights.push(`La diferencia entre tu mejor y peor mes es de ${diferencia.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}`);
    }
    
    return insights;
  }, [tendencias]);
  
  // Funciones para cambiar mes
  const irMesAnterior = useCallback(() => {
    setSelectedMonth(prev => {
      const newDate = new Date(prev.año, prev.mes - 1, 1);
      return { año: newDate.getFullYear(), mes: newDate.getMonth() };
    });
  }, []);
  
  const irMesSiguiente = useCallback(() => {
    const ahora = new Date();
    const mesActual = { año: ahora.getFullYear(), mes: ahora.getMonth() };
    
    setSelectedMonth(prev => {
      if (prev.año === mesActual.año && prev.mes === mesActual.mes) {
        return prev; // No avanzar más allá del mes actual
      }
      
      const newDate = new Date(prev.año, prev.mes + 1, 1);
      return { año: newDate.getFullYear(), mes: newDate.getMonth() };
    });
  }, []);
  
  const irMesActual = useCallback(() => {
    const ahora = new Date();
    setSelectedMonth({ año: ahora.getFullYear(), mes: ahora.getMonth() });
  }, []);
  
  const seleccionarMes = useCallback((año: number, mes: number) => {
    setSelectedMonth({ año, mes });
  }, []);
  
  // Función para obtener nombre del mes
  const getNombreMes = useCallback((mes: number): string => {
    const meses = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return meses[mes];
  }, []);
  
  // Estado de navegación
  const navegacion = useMemo(() => {
    const ahora = new Date();
    const esMesActual = selectedMonth.año === ahora.getFullYear() && 
                       selectedMonth.mes === ahora.getMonth();
    
    return {
      esMesActual,
      puedeRetroceder: true, // Siempre se puede retroceder
      puedeAvanzar: !esMesActual,
      tituloMes: `${getNombreMes(selectedMonth.mes)} ${selectedMonth.año}`
    };
  }, [selectedMonth, getNombreMes]);
  
  return {
    // Datos principales
    resumenActual,
    resumenSeleccionado,
    historialResumenes,
    datosComparativos,
    
    // Análisis y tendencias
    tendencias,
    insightsConsolidados,
    
    // Navegación
    selectedMonth,
    navegacion,
    irMesAnterior,
    irMesSiguiente,
    irMesActual,
    seleccionarMes,
    getNombreMes,
    
    // Estados
    loading: false, // Podríamos agregar loading states en el futuro
    error: null
  };
};